import { Assistants, Experiences, Filters, Search } from '@local/client-contracts';
import { isExtension, isGeneralAssistant, isAgenticAssistant } from '@local/common-web';
import { EventInfo, EventsService, LogService } from '@shared/services';
import { AssistantsService } from '@shared/services/assistants.service';
import { Logger } from '@unleash-tech/js-logger';
import { isEmpty } from 'lodash';
import { catchError, EMPTY, firstValueFrom, Subscription } from 'rxjs';
import { buildResourceLookupFromJson } from 'src/app/bar/utils/formatting-utils';
import {
  AnswerCompletionTypes,
  AnswerGenerateState,
  AnswerSearchItem,
  AnswerSearchReady,
  HeaderItem,
  SearchResults,
  StaticSearchItem,
  TelemetryTrigger,
} from 'src/app/bar/views/results/models/results-types';
import { AnswerResourcesService } from '../../../answer-resources.service';
import { AnswerSearchService } from '../../../answers-search.service';
import { FiltersService } from '../../../filters.service';
import { ResultMarkdownService } from '../../../result-markdown.service';
import { ResultsService } from '../../../results.service';
import { SearchClient } from '../search-client';
import { SearchRequest } from '../search-request';
import { SearchResponse } from '../search-response';
import { SearchResponseType } from '../search-response-type';
import { AnswersSourceSettings } from './answers-source-settings';
import { AnswersResultExtra } from './answers-result-extra';
import { Config } from '@environments/config';
import { RecentSearchesService } from 'src/app/bar/views/results/recent-searches.service';
import { ExperiencesInvokerService } from '../../../experiences-invoker.service';
import { SourceType } from 'src/app/bar/views/results/components/resource-panel-popup/resource-panel-popup.component';
import { AnswerResourceAgenticService } from '../../../answer-resource-agentic';

export class AnswersSearchClient implements SearchClient<AnswersSourceSettings> {
  private readonly SEPARATOR = /\s+/;
  private readonly INTERVAL_ANSWERS = 30;
  private readonly INTERVAL_EXTERNAL_ANSWERS = 30;
  private logger: Logger;
  private instances: { [sessionName: string]: Subscription } = {};
  private recentSearchTimeout: NodeJS.Timeout;

  constructor(
    logService: LogService,
    private resultsService: ResultsService,
    private resultMarkdownService: ResultMarkdownService,
    private assistantService: AssistantsService,
    private resourcesService: AnswerResourcesService,
    private answerSearchService: AnswerSearchService,
    private filtersService: FiltersService,
    private eventsService: EventsService,
    private recentSearches: RecentSearchesService,
    private experiencesInvokerService: ExperiencesInvokerService,
    private answerResourceAgenticService: AnswerResourceAgenticService
  ) {
    this.logger = logService.scope('answers');
  }

  supportsSort(_sort: Search.Sort): boolean {
    return true;
  }

  supportsFilters(_filters: Filters.Values): boolean {
    return true;
  }

  search(request: SearchRequest<AnswersSourceSettings>, response: SearchResponse): SearchResponseType {
    const subscription = this.instances[request.sessionName];
    if (subscription) {
      subscription.unsubscribe();
      this.instances[request.sessionName] = null;
    }
    return this.innerSearch(request, response);
  }

  nextPage(_request: SearchRequest<AnswersSourceSettings>, _response: SearchResponse, _trigger: TelemetryTrigger): Promise<void> {
    return;
  }

  destroy(id: number, sessionName: string): void {
    const subscription = this.instances[sessionName];
    if (subscription) {
      subscription?.unsubscribe();
      delete this.instances[sessionName];
    }
    this.resourcesService.clearResources();
  }

  private isQueryTooShort(query: string, sourceSettings: AnswersSourceSettings) {
    return !query || query.split(this.SEPARATOR).length < sourceSettings.minWords;
  }

  private async buildSearchAnswerRequest(request: SearchRequest<AnswersSourceSettings>, query: string) {
    let knowledgeType = Experiences.KnowledgeType.Internal;
    if (request.sourceSettings.assistantId) {
      const experience = await firstValueFrom(this.experiencesInvokerService.getExperience$(request.sourceSettings.assistantId, 'only'));
      knowledgeType = (isGeneralAssistant(experience) && experience?.settings?.general?.knowledgeType) || knowledgeType;
      if (isAgenticAssistant(experience)) {
        return this.answerSearchService.buildAgenticRequest(request, query);
      }
    }
    if (knowledgeType == Experiences.KnowledgeType.External) {
      return this.answerSearchService.buildExternalRequest(request, query);
    }

    const mergeFilters = request.sourceSettings.ignoreFilters ? null : this.filtersService.allFilters;
    if (mergeFilters && request.sourceSettings.collectionId) {
      mergeFilters['collectionId'] = [request.sourceSettings.collectionId];
    }
    return this.answerSearchService.buildInternalRequest(request, query, mergeFilters);
  }

  private async innerSearch(request: SearchRequest<AnswersSourceSettings>, response: SearchResponse): Promise<void> {
    const sourceSettings = request.sourceSettings;
    let globalAssistant: Experiences.ExperienceItem;

    const query: string = request.query?.trim();
    if (this.isQueryTooShort(query, sourceSettings) && !sourceSettings.blobIds?.length && isEmpty(sourceSettings.action || {})) {
      response.complete(true);
      return;
    }

    if (sourceSettings.shouldClassifyAi && sourceSettings.onlyClassifyAi && sourceSettings.assistantId) {
      globalAssistant = await firstValueFrom(this.experiencesInvokerService.getExperience$(sourceSettings.assistantId, 'only'));
      if (response.cancelled) {
        return;
      }
    }

    const req = await this.buildSearchAnswerRequest(request, query);
    if (response.cancelled) {
      return;
    }

    let renderResultsInterval: NodeJS.Timeout | null = null;
    const tasks: (() => void)[] = [];
    let searchDone = false;
    const reqFilters = (req as Assistants.InternalAnswersSearchRequest)?.filters;

    const cleanup = () => {
      if (renderResultsInterval) {
        clearInterval(renderResultsInterval);
        renderResultsInterval = null;
      }
      tasks.length = 0;
    };

    this.instances[request.sessionName] = this.assistantService
      .answers$(req)
      .pipe(
        catchError((error) => {
          this.logger.error('Search request failed:', error);
          searchDone = true;
          return EMPTY;
        })
      )
      .subscribe({
        next: (res) => {
          if (response.cancelled) {
            cleanup();
            return;
          }

          const task = () =>
            this.handleResults(
              res,
              request,
              response,
              query || req.query,
              req.knowledgeType,
              reqFilters,
              globalAssistant,
              req.experienceType
            );
          if (!AnswerGenerateState.includes(res.status)) {
            task();
            cleanup();
            return;
          }
          tasks.push(task);

          if (!renderResultsInterval) {
            renderResultsInterval = this.startTaskProcessingInterval(response, tasks, cleanup, req.knowledgeType, searchDone);
          }
        },
        error: () => {
          searchDone = true;
          cleanup();
        },
        complete: () => {
          searchDone = true;
        },
      });
  }

  private startTaskProcessingInterval(
    response: SearchResponse,
    tasks: (() => void)[],
    cleanup: () => void,
    knowledgeType: Experiences.KnowledgeType,
    searchDone: boolean
  ): NodeJS.Timeout {
    // set a fixed pace of interval even if backend returns a bulk of events at once
    const intervalDuration = knowledgeType === Experiences.KnowledgeType.External ? this.INTERVAL_EXTERNAL_ANSWERS : this.INTERVAL_ANSWERS;

    return setInterval(() => {
      if (response.cancelled || !tasks.length) {
        cleanup();
        return;
      }

      const currentTask = tasks.shift();
      if (!currentTask) {
        if (searchDone) {
          cleanup();
        }
        return;
      }

      currentTask();
    }, intervalDuration);
  }

  private handleResults(
    res: Assistants.AnswersSearchResponse,
    request: SearchRequest<AnswersSourceSettings>,
    response: SearchResponse,
    query: string,
    knowledgeType: Experiences.KnowledgeType,
    filters: Filters.Values,
    globalAssistant: Experiences.ExperienceItem,
    experienceType: Experiences.ExperienceType
  ) {
    let items = [];
    if (['IsAiResults', 'Cache'].includes(res.status)) {
      if (this.showAiResults(request)) {
        response.setPartialAliveSources(['answers']);
        this.addRecentSearch(query, this.filtersService.tagFilters, request.sessionId, response);
      }
    }
    switch (res.status) {
      case 'Skipped':
        break;
      case 'IsQuestion': {
        items = this.handleIsQuestion(request, items, filters, globalAssistant, res.searchId);
        break;
      }
      case 'NoResults':
      case 'RephraseRequired': {
        const noResultItem = this.answerSearchService.buildLoadingItem(query, res);
        items = [noResultItem];
        break;
      }
      case 'LoadingWithPreAnswer':
      case 'Loading': {
        if (this.showAiBox(request, res)) {
          const loadingItem = this.answerSearchService.buildLoadingItem(query, res);
          items = [loadingItem];
        }
        break;
      }
      case 'Error':
      case 'Result':
      case 'Generating':
      case 'GeneratingDone':
      case 'Full':
      case 'Cache':
        {
          if (isAgenticAssistant({ experienceType } as Experiences.ExperienceItemData)) {
            items = this.onAgenticAnswerReady(request, response, res, query);
          } else if (knowledgeType === Experiences.KnowledgeType.External) {
            items = this.onExternalAnswerReady(request, res, query);
          } else {
            items = this.onInternalAnswerReady(request, response, res, query);
          }
        }
        break;
    }
    if (request.sourceSettings.displayOpenChatResult && AnswerSearchReady.includes(res.status)) {
      const openChatItem: StaticSearchItem = this.answerSearchService.buildFollowUpItem(res);
      openChatItem.description = `Ask about anything in ${this.answerSearchService.sessionInfo()?.workspace?.name || 'N/A'}`;
      items.push(openChatItem);
    }
    response.extra = {} as AnswersResultExtra;
    if (res.searchId) {
      response.extra.searchId = res.searchId;
    }
    if (res.status) {
      response.extra.status = res.status;
    }
    if (this.showAiResults(request) && res.searchParameters) {
      response.extra.searchParameters = res.searchParameters;
    }
    response.items = items;
    if (['Loading', 'LoadingWithPreAnswer', 'SelfQuery', 'IsAiResults'].includes(res.status)) {
      response.notifyUpdated();
    } else {
      response.complete(true);
      if (AnswerCompletionTypes.includes(res.status)) {
        this.sendAnswerCompletedEvent(request.sourceSettings.type, request.sessionId, res.searchId, query, request.clientSearchId);
      }
    }
    if (res.status === 'Error') {
      this.answerSearchService.openToasterError();
    }
  }

  private sendAnswerCompletedEvent(type: string, sessionId: string, searchId: string, query: string, clientSearchId: string) {
    const searchEnd: Partial<EventInfo> = {
      label: 'answer_completed',
      search: {
        origin: type,
        query,
        sessionId,
        searchId,
        clientSearchId,
      },
    };
    this.eventsService.event('search.end', searchEnd);
  }

  private handleIsQuestion(
    request: SearchRequest<AnswersSourceSettings>,
    items: any[],
    filters: Filters.Values,
    globalAssistant: Experiences.ExperienceItem,
    searchId: string
  ) {
    let defaultSearchAnswerItem: StaticSearchItem;
    if (request.sourceSettings.assistantId) {
      defaultSearchAnswerItem = this.answerSearchService.buildAssistantAnswerStaticSearchItem(globalAssistant, 'search-answer', searchId);
    } else if (!this.isUnleashAiDisabledForQuickSearch()) {
      const defaultAssistant = this.experiencesInvokerService.defaultAssistant;
      defaultSearchAnswerItem = this.answerSearchService.buildAnswerStaticSearchItem(
        this.answerSearchService.sessionInfo(),
        searchId,
        defaultAssistant?.settings
      );
    }
    items = defaultSearchAnswerItem ? [defaultSearchAnswerItem] : [];
    const noFilters = isEmpty(filters);
    if (!request.sourceSettings.assistantId && noFilters) {
      const assistantItems = this.getQuestionAssistantsItems(searchId);
      items.push(...(assistantItems || []));
    }
    return items;
  }

  private getQuestionAssistantsItems(searchId: string): StaticSearchItem[] {
    const items: StaticSearchItem[] =
      this.answerSearchService
        .assistantForSearch()
        ?.map((a) => this.answerSearchService.buildAssistantAnswerStaticSearchItem(a, 'open-chat', searchId)) || [];
    items.sort((a, b) => {
      return a.title?.localeCompare(b.title);
    });
    return items;
  }

  private onInternalAnswerReady(
    request: SearchRequest<AnswersSourceSettings>,
    response: SearchResponse,
    res: Assistants.AnswersSearchResponse,
    query: string
  ): SearchResults[] {
    const showAiResults = this.showAiResults(request);

    const text = res.federatedAnswer?.answer;
    const items: Search.ResultResourceItem[] = this.buildResultItems(res, response, showAiResults);
    const selectedResourcesForAnswer = ['Knowledge', 'People'].includes(res.intent)
      ? this.buildSelectedResourcesForAnswer(res, items)
      : items;
    const formattedAnswer = this.buildFormattedAnswer(request, res, text, showAiResults, selectedResourcesForAnswer);

    this.resourcesService.resources = selectedResourcesForAnswer;

    const searchItems: SearchResults[] = [];

    if (this.showAiBox(request, res)) {
      const answerItem = this.buildAnswerItem(request, res, query, text, selectedResourcesForAnswer, formattedAnswer);
      searchItems.push(answerItem);
    }

    if (showAiResults && items.length) {
      searchItems.push(...items);
    }

    return searchItems;
  }

  private onAgenticAnswerReady(
    request: SearchRequest<AnswersSourceSettings>,
    response: SearchResponse,
    res: Assistants.AnswersSearchResponse,
    query: string
  ): SearchResults[] {
    let text: string;
    if (res.federatedAnswer) {
      text = res.federatedAnswer.answer;
    }
    const showAiResults = false;
    const items: Search.ResultResourceItem[] = this.buildResultItems(res, response, showAiResults);
    this.answerResourceAgenticService.updateResourceInCache(items);

    const answerItem = {
      type: 'answer',
      query,
      resources: [],
      references: res.federatedAnswer?.references || [],
      resourceIds: res.federatedAnswer?.resourceIds || [],
      text,
      state: res.status,
      debugInfo: res.debugInfo,
      searchId: res.searchId,
      sourcesList: items,
      formattedAnswer: text,
      tools: res.tools,
      downloadFiles: res.downloadFiles,
      agenticTools: res.agenticTools,
    } as AnswerSearchItem;

    return [answerItem];
  }

  private showAiBox(request: SearchRequest<AnswersSourceSettings>, res: Assistants.AnswersSearchResponse) {
    if (res.progress === 'isNotAi') {
      return false;
    }
    return this.showAiResults(request) ? ['Knowledge', 'People'].includes(res.intent) : true;
  }

  private showAiResults(request: SearchRequest<AnswersSourceSettings>) {
    return request.sourceSettings.source !== 'Chat' && !this.answerSearchService.isLauncher;
  }

  private buildResultItems(
    res: Assistants.AnswersSearchResponse,
    response: SearchResponse,
    showAiResults: boolean
  ): Search.ResultResourceItem[] {
    const items: SearchResults[] = res.results || [];

    for (const item of items) {
      item.action = this.resultsService.getResultAction(item);
      if (response.cancelled) {
        return;
      }
    }

    if (showAiResults && items.length) {
      const header: HeaderItem = {
        type: 'header',
        clickable: false,
        origin: 'answer',
        title: 'Results',
        titleEnd: `${items.length}`,
      };
      items.unshift(header);
    }

    return items as Search.ResultResourceItem[];
  }

  private buildSelectedResourcesForAnswer(
    res: Assistants.AnswersSearchResponse,
    items: Search.ResultResourceItem[]
  ): Search.ResultResourceItem[] {
    const resourceIds = res.federatedAnswer?.resourceIds;
    if (!resourceIds?.length || !AnswerSearchReady.includes(res.status)) {
      return [];
    }
    const uniqueIds = new Set();
    return items
      ?.filter((r) => {
        if (resourceIds.includes(r.id) && !uniqueIds.has(r.id)) {
          uniqueIds.add(r.id);
          return true;
        }
        return false;
      })
      .sort((a, b) => resourceIds.indexOf(a.resource.id) - resourceIds.indexOf(b.resource.id));
  }

  private buildFormattedAnswer(
    request: SearchRequest<AnswersSourceSettings>,
    res: Assistants.AnswersSearchResponse,
    formattedAnswer: string,
    isAiResults: boolean,
    resources: Search.ResultResourceItem[] | Search.ExternalSourceItem[],
    sourceType: SourceType = 'result-answer'
  ): string {
    if (request.sourceSettings.preventFormattedAnswer) {
      return formattedAnswer;
    }

    if (res.intent === 'ResourceLookup' && !isAiResults) {
      formattedAnswer = buildResourceLookupFromJson(resources, null, !isExtension());
    }

    const hasReferences = res.federatedAnswer?.references;

    if (hasReferences) {
      formattedAnswer = this.answerSearchService.insertReferencesIntoText(res.federatedAnswer, resources);
    }

    try {
      const markdown = this.resultMarkdownService.render(
        formattedAnswer || '',
        hasReferences?.length ? { items: resources, sourceType, searchId: res.searchId } : null
      );
      return markdown;
    } catch (error) {
      this.logger.error('Error rendering formatted answer:', error);
      return formattedAnswer;
    }
  }

  private buildAnswerItem(
    request: SearchRequest<AnswersSourceSettings>,
    res: Assistants.AnswersSearchResponse,
    query: string,
    text: string,
    selectedResources: Search.ResultResourceItem[],
    formattedAnswer: string
  ): AnswerSearchItem {
    if (!res.federatedAnswer && res.status === 'Result') {
      return this.answerSearchService.buildLoadingItem(query, res, 'Loading');
    }
    return {
      type: 'answer',
      query,
      text,
      resources: selectedResources,
      references: res.federatedAnswer?.references,
      resourceIds: res.federatedAnswer?.resourceIds,
      state: res.status,
      debugInfo: res.debugInfo,
      searchId: res.searchId,
      intent: res.intent,
      assistantId: request.sourceSettings.assistantId,
      formattedAnswer,
      searchParameters: res.searchParameters,
      effectiveQuery: res.effectiveQuery,
      agenticTools: res.agenticTools,
    } as AnswerSearchItem;
  }

  private onExternalAnswerReady(request: SearchRequest<AnswersSourceSettings>, res: Assistants.AnswersSearchResponse, query: string) {
    let text: string;
    if (res.federatedAnswer) {
      text = res.federatedAnswer.answer;
    }

    const answerItem = {
      type: 'answer',
      query,
      resources: [],
      references: res.federatedAnswer?.references || [],
      resourceIds: res.federatedAnswer?.sourcesIds || [],
      text,
      state: res.status,
      debugInfo: res.debugInfo,
      searchId: res.searchId,
      dataSources: res.results,
      formattedAnswer: text,
      tools: res.tools,
      downloadFiles: res.downloadFiles,
    } as AnswerSearchItem;

    return [answerItem];
  }

  private addRecentSearch(q: string, tagFilters: Filters.Values, sessionId: string, response: SearchResponse) {
    if (this.answerSearchService.sessionInfo()?.provider === 'unleash') {
      return;
    }
    if (this.recentSearchTimeout) {
      clearTimeout(this.recentSearchTimeout);
      this.recentSearchTimeout = undefined;
    }
    this.recentSearchTimeout = setTimeout(() => {
      if (response.cancelled) {
        return;
      }
      this.recentSearches.add(q?.trim(), tagFilters, 'search', sessionId);
    }, Config.recentSearches.debounce);
  }

  getTelemetryEndEvent(_response: SearchResponse): Partial<EventInfo>[] {
    const questionSuccess = AnswerSearchReady.includes(_response?.extra?.responseStatus);
    const endEvent: Partial<EventInfo> = { jsonData: { questionSuccess } };
    if (_response?.extra?.searchId) {
      endEvent.search = { searchId: _response.extra.searchId };
    }
    return [endEvent];
  }

  isUnleashAiDisabledForQuickSearch() {
    return (
      this.answerSearchService.sessionInfo()?.workspace?.settings?.assistant?.disabledUnleashAI === true ||
      this.experiencesInvokerService.defaultAssistant?.settings?.general?.displayOnQuickSearch !== true
    );
  }
}
